import { initializeApp, getApps } from "firebase/app";
import {
    ref,
    getDatabase,
    get as nativeGet,
    push as nativePush,
    update as nativeUpdate,
    remove as nativeRemove,
    onValue as nativeOnValue,
    off as nativeOff,
    set as nativeSet,
} from "firebase/database";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import CONFIG from "@bd-core/config";
import bdjs from "@bd-core/bdjs";
import bdapi from "@bd-core/bd-api";

const fb = (() => {
    const sessionRootPath = `${
        CONFIG.firebase?.environment
    }/sessions`;
    
    const sessionPath = CONFIG.firebase?.active_session_id 
        ? `${CONFIG.firebase?.environment}/sessions/${CONFIG.firebase?.team_code?.toUpperCase?.()}/${CONFIG.firebase?.active_session_id}`
        : null;
    
    const marksPath = sessionPath ? `${sessionPath}/marks` : null;

    /**
     * Get particular player path inside current team
     * @param {number} userId
     */
    const getMarkPath = (userId) => marksPath ? `${marksPath}/${userId}` : null;
    const currentMarkPath = getMarkPath(CONFIG.user_id);

    /**
     * @param {string} path
     */
    const getRefPath = (path) => path ? ref(getDatabase(), path) : null;
    const getRefSession = () => sessionPath ? getRefPath(sessionPath) : null;

    /**
     * Get new firebase token via ajax request
     */
    const getNewFirebaseToken = async () => {
        const [response, requestErr] = bdjs.goAsync(
            bdapi.post("game/firebase-token"),
        );

        if (requestErr) {
            return console.error("Failed to get new firebase token");
        }

        return response.data.data.token;
    };

    /**
     * Initialize firebase
     */
    const initFirebase = async () => {
        if (getApps().length === 0) {
            const config = {
                apiKey: CONFIG.firebase.key,
                authDomain: CONFIG.firebase.auth_domain,
                databaseURL: CONFIG.firebase.database_url,
            };

            initializeApp(config);
        }
        /**
         * NOTE: IMPORTANT
         * Avoid using promise.catch() for every firebase auth, you should always use
         * try catch block instead.
         * see:
         * https://github.com/firebase/firebase-js-sdk/issues/4242
         * https://github.com/firebase/firebase-js-sdk/issues/1881
         */
        try {
            await signInWithCustomToken(getAuth(), CONFIG.firebase.auth_token);
        } catch (err) {
            // in case error happen, Get new token and retry to initFirebase again
            const newToken = await getNewFirebaseToken();
            CONFIG.firebase.auth_token = newToken;
            return await initFirebase();
        }

        return true;
    };

    /**
     * Initialize game
     */
    const initializeGame = async () => {
        if (!CONFIG.firebase) {
            return;
        }

        bdjs.showLoading();

        const init = await bdjs.goAsync(initFirebase());
        const initError = init[1];

        if (initError) {
            console.error("Error initializing firebase app `initFirebase()`");
            return;
        }

        firebaseListener();
        return true;
    };

    /**
     * Register all firebase listener
     */
    const firebaseListener = async () => {
        const sessionRef = getRefSession();
        
        // If no active session, trigger empty initial state
        if (!sessionRef) {
            bdjs.trigger("bd__sessionInitialState", { data: null });
            return;
        }
        
        //* For listen initial state */
        // ----------------------------------------------------------------------------------
        nativeOnValue(
            sessionRef,
            (snapshot) => {
                const value = snapshot.val();
                bdjs.trigger("bd__sessionInitialState", { data: value });

                nativeOnValue(sessionRef, (snapshot) => {
                    const value = snapshot.val();
                    bdjs.trigger("bd__sessionDataChanged", { data: value });
                });
            },
            { onlyOnce: true },
        );
    };

    /* Firebase helper
     * =======================================================================================================================================
     */

    const get = (path) => {
        return nativeGet(getRefPath(path));
    };

    const push = (path, obj) => {
        return nativePush(getRefPath(path), obj);
    };

    const update = (path, obj) => {
        return nativeUpdate(getRefPath(path), obj);
    };

    const set = (path, obj) => {
        return nativeSet(getRefPath(path), obj);
    };

    const remove = (path) => {
        return nativeRemove(getRefPath(path));
    };

    const onValue = (path, callback) => {
        return nativeOnValue(getRefPath(path), callback);
    };

    const offValue = (path, callback) => {
        return nativeOff(getRefPath(path), "value", callback);
    };

    return {
        getMarkPath,
        initializeGame,
        get,
        set,
        push,
        update,
        remove,
        onValue,
        offValue,
        sessionPath,
        sessionRootPath,
        currentMarkPath,
    };
})();

export default fb;
